/**
 * TenantSearch — tiny reusable search input used across all Super
 * Admin tenant-listing screens (Tenants, Billing/Subs, Billing/Invoices,
 * Billing/Wallets, Firebase, etc.). Filters client-side over a list of
 * rows that have either ``name``/``slug`` (tenant docs) or
 * ``tenant_name``/``tenant_slug`` (subscription/wallet rows).
 *
 * Usage:
 *   const { query, setQuery, filter } = useTenantSearch();
 *   const visible = filter(rows);
 *   return (
 *     <>
 *       <TenantSearch value={query} onChange={setQuery} />
 *       {visible.map(...)}
 *     </>
 *   );
 *
 * Or for invoice rows where the tenant name comes from a join, pass a
 * picker via the second argument:
 *   filter(invoices, (inv) => tenantsById[inv.tenant_id]?.tenant_name)
 */
import { useCallback, useMemo, useState } from "react";
import { Search, X } from "lucide-react";

export function useTenantSearch(initial = "") {
    const [query, setQuery] = useState(initial);
    const filter = useCallback((rows, pickText) => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return (rows || []).filter((r) => {
            const text = pickText
                ? pickText(r)
                : [r.name, r.slug, r.tenant_name, r.tenant_slug, r.code, r.invoice_number]
                    .filter(Boolean).join(" ");
            return String(text || "").toLowerCase().includes(q);
        });
    }, [query]);
    return { query, setQuery, filter };
}

export default function TenantSearch({ value, onChange, placeholder = "Search tenants…", testid = "tenant-search" }) {
    const id = useMemo(() => `${testid}-input`, [testid]);
    return (
        <div className="relative w-full sm:w-72" data-testid={testid}>
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
                id={id}
                data-testid={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-card border border-border rounded-xl pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-primary"
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    data-testid={`${testid}-clear`}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
                >
                    <X size={12} />
                </button>
            )}
        </div>
    );
}
